import {
    AppRoute,
    AppRouter,
    AreAllPropertiesOptional,
    ClientArgs,
    ClientInferResponses,
    initClient,
    PartialClientInferRequest,
    Prettify,
} from "@ts-rest/core";
import { AxiosHeaders, AxiosResponse, ResponseType } from "axios";
import { backendClient } from "./backendClient";

function toResponseHeaders(
    rawHeaders: AxiosResponse["headers"],
): Record<string, string> {
    const headers: Record<string, string> = {};
    if (!rawHeaders) {
        return headers;
    }
    const setHeader = (rawValue: unknown, key: string) => {
        const normalizedKey = key.toLowerCase();
        if (
            typeof rawValue === "string" ||
            typeof rawValue === "number" ||
            typeof rawValue === "boolean"
        ) {
            headers[normalizedKey] = String(rawValue);
        } else if (Array.isArray(rawValue)) {
            headers[normalizedKey] = rawValue.join(", ");
        }
    };
    const headerEntries =
        rawHeaders instanceof AxiosHeaders ? rawHeaders.toJSON() : rawHeaders;
    for (const [key, value] of Object.entries(headerEntries)) {
        setHeader(value, key);
    }
    return headers;
}

export function getBackendAPIClient<T extends AppRouter>(
    contract: T,
    responseType?: ResponseType,
    clientArgs?: Omit<ClientArgs, "baseUrl">,
): FilteredRecursiveProxyObj<T> {
    return initClient(contract, {
        baseUrl: "",
        api: async ({ path, method, body, headers }) => {
            let response: AxiosResponse<unknown, unknown>;
            if (method === "GET") {
                response = await backendClient.get(`${path}`, {
                    headers,
                    params: body,
                    paramsSerializer: { indexes: true },
                    responseType,
                });
            } else if (["POST", "PUT", "PATCH"].includes(method)) {
                response = await backendClient[
                    [method.toLowerCase()] as unknown as
                        | "post"
                        | "put"
                        | "patch"
                ](`${path}`, body, {
                    headers,
                    responseType,
                });
            } else if (method === "DELETE") {
                response = await backendClient.delete(`${path}`, {
                    data: body,
                    headers,
                    responseType,
                });
            } else {
                throw new Error(`Unsupported method: ${method}`);
            }

            return {
                status: response.status,
                body: response?.data,
                // ts-rest's ApiFetcher types `headers` as a native `Headers`,
                // but we deliberately return a plain, structured-cloneable
                // object so React Query's cross-tab BroadcastChannel sync does
                // not throw DataCloneError. Consumers see it as a plain record
                // via FilteredClientInferResponses below.
                headers: toResponseHeaders(
                    response.headers,
                ) as unknown as Headers,
            };
        },
        ...clientArgs,
    }) as FilteredRecursiveProxyObj<T>;
}

// NOTE: This is needed because by default the client inferred responses contain all the status codes,
// while we only expect 200 or 201.
// Also note that backendClient throws an error if the status code is not in the 2xx range and)

// Utility type to extract the allowed status codes (200 and 201) from the route's responses
type AllowedStatusCodesForRoute<TRoute extends AppRoute> = Extract<
    keyof TRoute["responses"],
    200 | 201
>;

type FilteredClientInferResponses<TRoute extends AppRoute> =
    ClientInferResponses<TRoute> extends infer R
        ? R extends { status: infer Status }
            ? Status extends AllowedStatusCodesForRoute<TRoute>
                ? Omit<R, "headers"> & { headers: Record<string, string> }
                : never
            : never
        : never;

// Adjusted filtered AppRouteFunction that uses the filtered responses and allowed status codes
type FilteredAppRouteFunction<
    TRoute extends AppRoute,
    TClientArgs extends ClientArgs,
    TArgs = PartialClientInferRequest<TRoute, TClientArgs>,
> =
    AreAllPropertiesOptional<TArgs> extends true
        ? (
              args?: Prettify<TArgs>,
          ) => Promise<Prettify<FilteredClientInferResponses<TRoute>>>
        : (
              args: Prettify<TArgs>,
          ) => Promise<Prettify<FilteredClientInferResponses<TRoute>>>;

// Adjusted recursive proxy object type that uses the filtered AppRouteFunction
type FilteredRecursiveProxyObj<
    T extends AppRouter,
    TClientArgs extends ClientArgs = ClientArgs,
> = {
    [TKey in keyof T]: T[TKey] extends AppRoute
        ? FilteredAppRouteFunction<T[TKey], TClientArgs>
        : T[TKey] extends AppRouter
          ? FilteredRecursiveProxyObj<T[TKey], TClientArgs>
          : never;
};
